import React, { useCallback, useId, useMemo } from 'react'
import LoadingSpinner from '../../ui/LoadingSpinner'
import { measurementDefinitions } from '../../../utils/measurementDefinitions'

const MeasurementSelector = React.memo(({ availableMeasurements, selectedMeasurements, onChange, loading = false, error = null }) => {
  const baseId = useId()

  const handleMeasurementChange = useCallback((measurement, isChecked) => {
    onChange(
      isChecked
        ? [...selectedMeasurements, measurement]
        : selectedMeasurements.filter(m => m !== measurement),
    )
  }, [selectedMeasurements, onChange])

  const metricMeasurements = useMemo(() => {
    return availableMeasurements.filter(
      m => measurementDefinitions[m]?.usage === 'Metric',
    )
  }, [availableMeasurements])

  const handleSelectAllMeasurements = useCallback(() => {
    if (metricMeasurements.length === selectedMeasurements.filter(m => metricMeasurements.includes(m)).length) {
      onChange(selectedMeasurements.filter(m => !metricMeasurements.includes(m)))
    } else {
      onChange([...new Set([...selectedMeasurements, ...metricMeasurements])])
    }
  }, [metricMeasurements, selectedMeasurements, onChange])

  if (loading) return <LoadingSpinner message="Loading measurements..." />
  if (error) return <div className="alert alert-danger py-2"><i className="bi bi-exclamation-triangle me-2"></i>{error}</div>

  const allMetricMeasurementsSelected = metricMeasurements.length > 0 && 
                                      metricMeasurements.every(m => selectedMeasurements.includes(m))

  return (
    <div className="measurement-selector">
      <div className="d-flex align-items-center mb-3">
        <h6 className="mb-0 fw-bold">Select Measurements:</h6>
        {metricMeasurements.length > 0 && (
          <button
            className="btn btn-outline-secondary btn-sm ms-3"
            onClick={handleSelectAllMeasurements}
            disabled={metricMeasurements.length === 0}
          >
            <i className="bi bi-check-all me-1"></i>
            {allMetricMeasurementsSelected ? 'Deselect All' : 'Select All'}
          </button>
        )}
      </div>
      <div className="d-flex flex-wrap gap-2 mb-3">
        {metricMeasurements.length > 0 ? metricMeasurements.map(measurement => (
          <React.Fragment key={measurement}>
            <input
              type="checkbox"
              className="btn-check"
              id={`${baseId}-measurement-${measurement}`}
              checked={selectedMeasurements.includes(measurement)}
              onChange={e => handleMeasurementChange(measurement, e.target.checked)}
              autoComplete="off"
            />
            <label 
              className={`btn btn-sm ${selectedMeasurements.includes(measurement) ? 'btn-primary' : 'btn-outline-primary'}`}
              htmlFor={`${baseId}-measurement-${measurement}`}
            >
              {measurementDefinitions[measurement]?.friendlyName || measurement}
            </label>
          </React.Fragment>
        )) : (
          <div className="text-muted">No &apos;Metric&apos; measurements available for selected devices</div>
        )}
      </div>
    </div>
  )
})

MeasurementSelector.displayName = 'MeasurementSelector'

export default MeasurementSelector